import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { appConfig } from '@/api/appConfig';
import type { M3ApiResponse } from '@/types/api';
import type {
  AddWithdrawFundsRecord,
  ConfirmUserOperationRequest,
  GetUserOperationsRecordsRequest,
  StatsAddWithdrawFundsResponse,
} from '@/types/user-operations';

// Re-export the canonical payment-intermediaries query.
export { usePaymentIntermediariesQuery } from '@/features/payment-intermediaries/api';

export const USER_OPS_KEYS = {
  records: (req: GetUserOperationsRecordsRequest) => ['user-operations', 'records', req] as const,
  stats: (status: string) => ['user-operations', 'stats', status] as const,
};

export function useUserOperationsQuery(request: GetUserOperationsRecordsRequest) {
  return useQuery({
    queryKey: USER_OPS_KEYS.records(request),
    queryFn: async () => {
      const { data } = await apiClient.post<M3ApiResponse<AddWithdrawFundsRecord[]>>(
        `${appConfig.useroperationApi}/getUserOperationsRecords`,
        request,
      );
      if (data.code !== 0) throw new Error(data.message);
      return data.result ?? [];
    },
  });
}

export function useUserOperationsStatsQuery(status: string) {
  return useQuery({
    queryKey: USER_OPS_KEYS.stats(status),
    queryFn: async () => {
      const { data } = await apiClient.get<M3ApiResponse<StatsAddWithdrawFundsResponse>>(
        `${appConfig.useroperationApi}/statsUserOperationsFunds/${status}`,
      );
      return data.result;
    },
  });
}

export function useConfirmUserOperationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (formData: FormData) => {
      const { data } = await apiClient.post<M3ApiResponse<AddWithdrawFundsRecord>>(
        `${appConfig.useroperationApi}/confirmUserOperation`,
        formData,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-operations'] });
    },
  });
}

export function useCancelUserOperationMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (request: ConfirmUserOperationRequest) => {
      const { data } = await apiClient.post<M3ApiResponse<AddWithdrawFundsRecord>>(
        `${appConfig.useroperationApi}/cancelUserOperation`,
        request,
      );
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['user-operations'] });
    },
  });
}
